import {RouteProp, useRoute} from '@react-navigation/native';
import React from 'react';
import {TouchableOpacity} from 'react-native';

import Card from '@/components/Card/Card';
import DynamicImage from '@/components/DynamicImage/DynamicImage';
import InfinityList, {InfinityListEmpty} from '@/components/InfinityList';
import PlayerBar from '@/components/PlayerBar/PlayerBar';
import Screen from '@/components/Screen/Screen';
import ScreenError from '@/components/ScreenError';
import ScreenLoader from '@/components/ScreenLoader';
import Space from '@/components/Space/Space';
import Text from '@/components/Text/Text';
import TrackCardEmbed, {
  EMBED_TRACK_CARD_HEIGHT,
} from '@/components/TrackCard/TrackCardEmbed';
import {useEmbedColorsListener} from '@/hooks/useEmbedColorsListener';
import {useEmbedPlayer} from '@/hooks/useEmbedPlayer';
import {useEmbedArtistQuery} from '@/queries/artists';
import {useCustomThemesConfigQuery} from '@/queries/themes';
import {useCustomColors, useThemedStyles} from '@/theme';
import {useOverrideTheme} from '@/theme/useOverrideTheme';
import {EmbedStackParams, Routes} from '@/types/routes';
import {getArtistUrl} from '@/utils/share';

import {styles} from './Artist.style';

const EmbedArtist = () => {
  const style = useThemedStyles(styles);
  const {
    params: {id, theme, colors},
  } = useRoute<RouteProp<EmbedStackParams, Routes.EmbedArtist>>();
  const {
    artist,
    tracks,
    query: {isLoading: embedArtistQueryIsLoading, isError},
  } = useEmbedArtistQuery(id);

  const {
    currentTrack,
    isPlaying,
    togglePlay,
    playTrack,
    hasNext,
    next,
    previous,
  } = useEmbedPlayer(tracks);

  const {
    artistThemes,
    query: {isLoading: customThemesConfigIsLoading},
  } = useCustomThemesConfigQuery(true);

  const isLoading = embedArtistQueryIsLoading || customThemesConfigIsLoading;

  useOverrideTheme(
    // prioritize theme from url param
    theme
      ? {predefinedThemeName: theme}
      : {
          customTheme: artist?.customTheme,
          predefinedThemeName:
            artist && (artist.predefinedThemeName || artistThemes[artist.id]),
        },
  );

  useCustomColors(colors);
  useEmbedColorsListener();

  const onArtistPress = () => {
    if (artist) {
      window.open(getArtistUrl(artist.slug));
    }
  };

  if (isLoading) {
    return <ScreenLoader />;
  }

  if (!artist) {
    return <ScreenError textId="notFound" />;
  }

  const ListHeaderComponent = (
    <>
      <Space style={style.header}>
        {!!artist.avatarUrl && (
          <Card style={style.avatarWrapper}>
            <DynamicImage
              source={{uri: artist.avatarUrl}}
              style={style.avatar}
              hasBorderRadius
            />
          </Card>
        )}
        <Space>
          <TouchableOpacity onPress={onArtistPress}>
            <Text size="l" weight="semibold">
              {artist.name}
            </Text>
          </TouchableOpacity>
          <Space mt="xxs" />
          <TouchableOpacity onPress={onArtistPress}>
            <Text size="xs" id="embed.listen" />
          </TouchableOpacity>
        </Space>
      </Space>
      <Space mt="s" />
    </>
  );

  return (
    <Screen>
      <InfinityList
        itemSize={EMBED_TRACK_CARD_HEIGHT}
        data={tracks}
        keyExtractor={track => track.id}
        renderItem={({item: track}) => (
          <TrackCardEmbed
            track={track}
            isCurrentTrack={track.id === currentTrack?.id}
            isPlaying={isPlaying}
            onTogglePlay={togglePlay}
            onPlay={() => playTrack(track)}
          />
        )}
        ListHeaderComponent={ListHeaderComponent}
        ListEmptyComponent={<InfinityListEmpty isError={isError} />}
      />
      <PlayerBar
        currentTrack={currentTrack}
        isPlaying={isPlaying}
        onPlayPress={togglePlay}
        onNextPress={next}
        onPreviousPress={previous}
        hasNext={hasNext}
        fullPlayerEnabled={false}
      />
    </Screen>
  );
};

export default EmbedArtist;
